import Icon from '@mui/material/Icon'
import React from 'react'

import Button from '@/app/component/atom/button'
import { type CampaignItem } from '@/app/module/campaigns/types'

type Props = {
  campaign: Pick<CampaignItem, 'open' | 'published'>
  isLoading: boolean
  isMobile: boolean
  root: 'groups' | 'segments'
  selectedGroupIds: number[]

  onSubscribe: (groupIds: number[]) => Promise<void>
  onUnsubscribe: (groupIds: number[]) => Promise<void>
}

const GroupActions: React.FC<Props> = (props) => {
  const { campaign, isLoading, isMobile, root, selectedGroupIds, onSubscribe, onUnsubscribe } = props
  const itemLabel = root === 'segments' ? 'segment' : 'group'
  const label = root === 'segments' ? 'Segments' : 'Groups'
  const selectedCount = selectedGroupIds.length
  const subscribeDisabledReason = getSubscribeGroupsDisabledReason({
    campaignOpen: campaign.open,
    campaignPublished: campaign.published,
    isLoading,
    itemLabel,
    selectedCount,
  })
  const unsubscribeDisabledReason = getUnsubscribeGroupsDisabledReason({
    campaignPublished: campaign.published,
    isLoading,
    itemLabel,
    selectedCount,
  })

  const handleSubscribeSelected = React.useCallback(
    () => onSubscribe(selectedGroupIds),
    [selectedGroupIds, onSubscribe],
  )
  const handleUnsubscribeSelected = React.useCallback(
    () => onUnsubscribe(selectedGroupIds),
    [selectedGroupIds, onUnsubscribe],
  )

  return (
    <>
      <Button
        disabled={!!subscribeDisabledReason}
        startIcon={isMobile ? undefined : <Icon>input</Icon>}
        tooltip={subscribeDisabledReason}
        variant="outlined"
        onClick={handleSubscribeSelected}
      >
        {isMobile ? 'Subscribe' : `Subscribe ${label}`}
      </Button>
      <Button
        disabled={!!unsubscribeDisabledReason}
        startIcon={isMobile ? undefined : <Icon>remove_circle</Icon>}
        tooltip={unsubscribeDisabledReason}
        variant="outlined"
        onClick={handleUnsubscribeSelected}
      >
        {isMobile ? 'Unsubscribe' : `Unsubscribe ${label}`}
      </Button>
    </>
  )
}

const getLoadingDisabledReason = (isLoading: boolean) =>
  isLoading ? 'Please wait until the current subscription action is finished.' : ''

const getSubscribeGroupsDisabledReason = ({
  campaignOpen,
  campaignPublished,
  isLoading,
  itemLabel,
  selectedCount,
}: {
  campaignOpen: boolean
  campaignPublished: boolean
  isLoading: boolean
  itemLabel: string
  selectedCount: number
}) => {
  if (!campaignPublished) {
    return 'The campaign must be live before you can subscribe contacts.'
  }

  if (!campaignOpen) {
    return 'Please "Enable new subscriptions" in the Actions menu on the Campaigns page to be able to subscribe contacts.'
  }

  if (selectedCount === 0) {
    return `Please select a ${itemLabel}.`
  }

  return getLoadingDisabledReason(isLoading)
}

const getUnsubscribeGroupsDisabledReason = ({
  campaignPublished,
  isLoading,
  itemLabel,
  selectedCount,
}: {
  campaignPublished: boolean
  isLoading: boolean
  itemLabel: string
  selectedCount: number
}) => {
  if (!campaignPublished) {
    return 'The campaign must be live before you can unsubscribe contacts.'
  }

  if (selectedCount === 0) {
    return `Please select a ${itemLabel}.`
  }

  return getLoadingDisabledReason(isLoading)
}

export default GroupActions
